import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash, PencilSimple, FolderSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MATERIAL_FAMILIES, FAMILY_ORDER, CLASS_DEFAULT_FAMILY } from "@/lib/materials";

const empty = { id: null, name: "", klass: "otro", material_family: "plastic", subcategories: [], primary_collection_ids: [], general_collection_ids: [] };
const CLASSES = [["joyeria", "Joyería"], ["ropa", "Ropa"], ["accesorio", "Accesorio"], ["mueble", "Mueble"], ["modelo3d", "Modelo 3D"], ["ilustracion", "Ilustración"], ["otro", "Otro"]];

// Product categories (e.g. "Sortija de graduación", "Compromiso"). Each holds default primary
// and general collections that its products inherit (overridable per product).
export default function CategoriesManager() {
  const [cats, setCats] = useState([]);
  const [cols, setCols] = useState([]);
  const [form, setForm] = useState(null);
  const [subInput, setSubInput] = useState("");

  const load = () => Promise.all([
    api.get("/categories").then((r) => setCats(r.data || [])),
    api.get("/collections").then((r) => setCols(r.data || [])),
  ]).catch(() => {});
  useEffect(() => { load(); }, []);

  const primaryCols = cols.filter((c) => (c.role || (c.type === "university" ? "primary" : "general")) === "primary");
  const generalCols = cols.filter((c) => (c.role || (c.type === "university" ? "primary" : "general")) === "general");

  const toggle = (key, id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));
  const addSub = () => { const v = subInput.trim(); if (!v) return; setForm((f) => ({ ...f, subcategories: f.subcategories.includes(v) ? f.subcategories : [...f.subcategories, v] })); setSubInput(""); };
  const removeSub = (v) => setForm((f) => ({ ...f, subcategories: f.subcategories.filter((x) => x !== v) }));

  const save = async () => {
    if (!form.name.trim()) return;
    try {
      const payload = { name: form.name.trim(), klass: form.klass, subtype: null, material_family: form.material_family, subcategories: form.subcategories, primary_collection_ids: form.primary_collection_ids, general_collection_ids: form.general_collection_ids };
      if (form.id) await api.put(`/categories/${form.id}`, payload);
      else await api.post("/categories", payload);
      setForm(null); setSubInput(""); await load(); toast.success("Categoría guardada");
    } catch (e) { toast.error(apiError(e) || "No se pudo guardar"); }
  };
  const del = async (id) => { if (!window.confirm("¿Borrar esta categoría?")) return; try { await api.delete(`/categories/${id}`); await load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="border border-white/10 rounded-xl p-5 bg-[#121212] space-y-4" data-testid="categories-manager">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2"><FolderSimple size={16} className="text-gold" /> Categorías de producto</h3>
        <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => setForm({ ...empty })} data-testid="add-category-btn"><Plus size={14} className="mr-1" /> Nueva</Button>
      </div>
      <p className="text-xs text-neutral-500">Ej: Ropa, Muebles, Accesorios, Joyería. Cada categoría define un material por defecto y sus subcategorías (ej. Ropa → Camisetas · Tela). Los productos de la categoría heredan estos valores (editables por producto).</p>

      <div className="flex flex-wrap gap-2">
        {cats.length === 0 && <span className="text-xs text-neutral-500">Aún no tienes categorías.</span>}
        {cats.map((c) => (
          <div key={c.id} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm" data-testid={`category-${c.id}`}>
            <span>{c.name}</span>
            <span className="text-[10px] text-gold/80">{(MATERIAL_FAMILIES[c.material_family || "plastic"] || {}).label}</span>
            {(c.subcategories?.length || 0) > 0 && <span className="text-[10px] text-neutral-500">{c.subcategories.length} subcat.</span>}
            <button onClick={() => { setForm({ id: c.id, name: c.name, klass: c.klass || "otro", material_family: c.material_family || "plastic", subcategories: c.subcategories || [], primary_collection_ids: c.primary_collection_ids || [], general_collection_ids: c.general_collection_ids || [] }); setSubInput(""); }} className="text-neutral-400 hover:text-gold"><PencilSimple size={13} /></button>
            <button onClick={() => del(c.id)} className="text-neutral-400 hover:text-red-400"><Trash size={13} /></button>
          </div>
        ))}
      </div>

      {form && (
        <div className="border-t border-white/10 pt-4 space-y-3" data-testid="category-form">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre de la categoría (ej. Ropa)" className="bg-white/5 border-white/15 h-9" data-testid="category-name-input" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-neutral-400">Tipo de producto</label>
              <select value={form.klass} onChange={(e) => { const k = e.target.value; setForm({ ...form, klass: k, material_family: CLASS_DEFAULT_FAMILY[k] || form.material_family }); }}
                className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200" data-testid="category-class-select">
                {CLASSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-neutral-400">Material por defecto</label>
              <select value={form.material_family} onChange={(e) => setForm({ ...form, material_family: e.target.value })}
                className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200" data-testid="category-material-select">
                {FAMILY_ORDER.map((f) => <option key={f} value={f}>{MATERIAL_FAMILIES[f].label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] text-neutral-400">Subcategorías (ej. Camisetas, Pantalones)</label>
            <div className="flex gap-2 mt-1">
              <Input value={subInput} onChange={(e) => setSubInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }} placeholder="Escribe y pulsa +" className="bg-white/5 border-white/15 h-9" data-testid="category-subcat-input" />
              <Button size="sm" variant="outline" className="rounded-lg border-white/15 h-9" onClick={addSub} data-testid="category-subcat-add"><Plus size={14} /></Button>
            </div>
            {form.subcategories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.subcategories.map((s) => (
                  <span key={s} className="flex items-center gap-1 bg-gold/15 text-gold text-xs rounded-full pl-2.5 pr-1 py-1" data-testid={`category-subcat-${s}`}>
                    {s}<button onClick={() => removeSub(s)} className="hover:text-white"><X size={12} weight="bold" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[["primary_collection_ids", "Colecciones destacadas", primaryCols], ["general_collection_ids", "Colecciones generales", generalCols]].map(([key, label, list]) => (
              <div key={key}>
                <div className="text-xs font-semibold text-neutral-400 mb-1">{label}</div>
                <div className="space-y-1 max-h-40 overflow-auto pr-1">
                  {list.length === 0 && <span className="text-[11px] text-neutral-600">Ninguna</span>}
                  {list.map((c) => {
                    const on = form[key].includes(c.id);
                    return (
                      <button key={c.id} onClick={() => toggle(key, c.id)} data-testid={`cat-${key}-${c.id}`}
                        className={`w-full flex items-center justify-between text-left text-xs rounded px-2 py-1 ${on ? "bg-gold/15 text-gold" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}>
                        <span className="truncate">{c.name}{c.group_label ? ` · ${c.group_label}` : ""}</span>
                        {on && <Check size={12} weight="bold" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-gold text-obsidian hover:bg-gold/90" onClick={save} data-testid="save-category-btn">Guardar</Button>
            <Button size="sm" variant="ghost" onClick={() => setForm(null)}>Cancelar</Button>
          </div>
        </div>
      )}
    </div>
  );
}
